import type { Store } from 'effector'
import type { VodRecommendedWithPrice } from '~/shared/interfaces'
import type { MovieCategoryWithRecommendedFormatted } from './index.h'

import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { not } from 'patronum'
import { model as i18nModel } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as movieModel } from '~/entities/movies'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import { TrailerContentType } from '~/entities/player/trailer'
import { isFree } from '~/features/payment'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import * as pinLockFeature from '~/features/pin-lock'
import { profilesFeaturesModel } from '~/features/profiles'
import { model as guestModel } from '~/features/subscriber/guest'
import { F, T, createUnsettledDetailsLoading } from '~/shared/helpers'
import '~/widgets/Player/PlayerTrailer'
import { extendMediaItemByLabelPrice, resolveNewLabel } from '~/shared/tools'
import { model as popupModel } from '~/widgets/Popups/InfoPopup'

export const init = createEvent<{
  vodId: string
  categoryId: number
  vodIndex?: number
  query: URLSearchParams
}>()

export const left = createEvent<{
  vodId: number
}>()

export const $isOnThePage = createStore(false).on(init, T).on(left, F)

export const $isDetailsPageLoading = createUnsettledDetailsLoading({
  $isOnThePage,
  $entity: movieModel.$vodFormatted,
  $pending: api.vods.getVodFx.pending,
  settled: api.vods.getVodFx.finally,
  failed: api.vods.getVodFx.fail,
  resetOn: [init, left],
})

const shouldShowAlsoMayLikeSection = (
  isKids: boolean,
  query: URLSearchParams
) =>
  query.get('fromCwActionsDetails') !== 'true' &&
  query.get('fromSearch') !== 'true' &&
  !(
    isKids &&
    !query.get('collectionId') &&
    query.get('only-favorites') !== 'true'
  )

export const $shouldShowAlsoMayLike = createStore(true).reset(left)

sample({
  clock: init,
  source: profilesFeaturesModel.$isActiveKidsProfile,
  fn: (isKids, { query }) => shouldShowAlsoMayLikeSection(isKids, query),
  target: $shouldShowAlsoMayLike,
})

sample({
  clock: init,
  filter: not(api.vods.getVodFx.pending),
  fn: ({ vodId }) => ({ page: 0, vodId: Number(vodId) }),
  target: api.vods.getVodFx,
})

sample({
  clock: api.vods.updateContinueWatchingFx.done,
  source: init,
  filter: not(api.vods.updateContinueWatchingFx.pending),
  fn: ({ vodId }) => ({ page: 0, vodId: Number(vodId) }),
  target: api.vods.getVodFx,
})

sample({
  clock: init,
  source: {
    pending: api.vods.getRecommendedVodsFx.pending,
    isKids: profilesFeaturesModel.$isActiveKidsProfile,
  },
  filter: ({ pending, isKids }, { query }) =>
    !pending && shouldShowAlsoMayLikeSection(isKids, query),
  fn: (_, { vodId, query }) => ({
    vodId: Number(vodId),
    categoryId: Number(query.get('categoryId') || 0),
    collectionId: query.get('collectionId') || undefined,
    'only-favorites': query.get('only-favorites') === 'true' ? true : undefined,
    vodIndex: query.has('showIndex')
      ? Number(query.get('showIndex'))
      : undefined,
  }),
  target: api.vods.getRecommendedVodsFx,
})

export const $queryCollectionId = createStore<string>('').on(
  init,
  (_, { query }) => {
    const collectionId = query.get('collectionId')
    return collectionId ? `&collectionId=${collectionId}` : ''
  }
)

export const $queryOnlyFavorites = createStore<string>('').on(
  init,
  (_, { query }) =>
    query.get('only-favorites') === 'true' ? '&only-favorites=true' : ''
)

export const $movieCategoryWithRecommendedFormatted: Store<MovieCategoryWithRecommendedFormatted | null> =
  combine(
    api.vods.$movieCategoryWithRecommended,
    ssePurchasesModel.$vod,
    i18nModel.$t,
    (category, adjust, { t }) => {
      if (category) {
        return {
          ...category,
          content: category?.content.map((vod) => {
            const formatted = extendMediaItemByLabelPrice(
              adjust(vod)
            ) as VodRecommendedWithPrice

            return {
              ...formatted,
              newLabel: resolveNewLabel(formatted, t),
            }
          }),
        }
      }
      return category
    }
  )

const $canToggleWatched = combine(
  movieModel.$vodFormatted,
  guestModel.$guest,
  (vod, guest) => Boolean(vod) && !guest && isFree(vod)
)

const $canMarkAsWatched = combine(
  $canToggleWatched,
  movieModel.$vodFormatted,
  (canToggleWatched, vod) => canToggleWatched && !vod?.watched
)

const $canUnmarkAsWatched = combine(
  $canToggleWatched,
  movieModel.$vodFormatted,
  (canToggleWatched, vod) => canToggleWatched && Boolean(vod?.watched)
)

//
// Unlock pin locked movie
//

const unlock = createEvent()

sample({
  clock: unlock,
  source: movieModel.$vodFormatted,
  filter: Boolean,
  fn: ({ id, name, locked }) =>
    ({
      ...pinLockFeature.emptyPinLockState,
      id,
      name,
      locked,
    }) as pinLockFeature.IPinLockPopupProps,
  target: pinLockFeature.openPinLockPopup,
})

//
//
//

// clear store
api.vods.$vod.reset(init, left)

sample({
  clock: init,
  target: popupModel.closeInfoPopup,
})

register(movieModel.$vodFormatted, '$vodFormatted')
register(api.vods.getVodFx.pending, '$routes/movies/details/loading')
register(
  $movieCategoryWithRecommendedFormatted,
  '$movieCategoryWithRecommended'
)
register($queryCollectionId, '$movieCollectionId')
register($queryOnlyFavorites, '$movieOnlyFavorites')
register($canMarkAsWatched, '$routes/movies/details/canMarkAsWatched')
register($canUnmarkAsWatched, '$routes/movies/details/canUnmarkAsWatched')
register($shouldShowAlsoMayLike, '$routes/movies/details/shouldShowAlsoMayLike')
register(unlock, 'routes/movies/details/unlock')

//
// Trailer auto-playback logic
//

// Start trailer flow when VOD data is loaded and content is not locked
sample({
  clock: api.vods.getVodFx.doneData,
  source: {
    isLockedFn: pinLockFeature.$isLockedFn,
    isFlowActive: entitiesPlayerTrailerModel.$isTrailerFlowActive,
    isOnTrailerPage: $isOnThePage,
  },
  filter: ({ isLockedFn, isFlowActive, isOnTrailerPage }, vod) => {
    if (!isOnTrailerPage) return false
    if (!vod || isFlowActive) return false
    // Check if VOD has trailer and is not locked
    return Boolean(vod.trailerPresent) && !isLockedFn(vod)
  },
  fn: ({ isLockedFn }, vod) => ({
    contentType: TrailerContentType.VOD,
    contentId: vod.id,
    hasTrailer: true,
    locked: isLockedFn(vod),
    free: vod.free,
    contentName: vod.name,
    categoryName: 'Movie',
  }),
  target: entitiesPlayerTrailerModel.initTrailerFlow,
})

// Stop trailer flow when leaving the page
sample({
  clock: [left, api.vods.getVodFx],
  target: entitiesPlayerTrailerModel.stopTrailerFlow,
})
